import { RemoveEmailCycleRecipientRPC } from "@pulse/pulse-rpcs/lib/email-cycle/removeEmailCycleRecipientRPC";
import { LeoRPCResult, LeoUUID } from "@surya-digital/leo-ts-types";
import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { getAPIClient } from "../../networking/APIClient";
import { useRemoveEmailCycleRecipientRPCClientImpl } from "../rpcs/RPC";

export const EMAIL_CYCLE_RECIPIENTS_PER_PAGE = 10;

const getInitialState = (): SnapshotIn<
  typeof RemoveEmailCycleRecipientStore
> => ({
  loadingRemoveEmailCycleRecipient: false,
  removeEmailCycleRecipientRPCError: null,
});

const RecipientDetailsModel = types.model("RecipientDetails", {
  emailId: types.string,
  email: types.string,
});

export const RemoveEmailCycleRecipientStore = types
  .model("RemoveEmailCycleRecipientStore", {
    recipientDetails: types.maybeNull(RecipientDetailsModel),
    loadingRemoveEmailCycleRecipient: types.boolean,
    removeEmailCycleRecipientRPCError: types.maybeNull(
      types.enumeration(
        "RemoveEmailCycleRecipientRPCError",
        Object.values(RemoveEmailCycleRecipientRPC.RPCError),
      ),
    ),
  })
  .actions((store) => ({
    setRecipientDetails: (emailId: string, email: string): void => {
      store.recipientDetails = RecipientDetailsModel.create({
        emailId,
        email,
      });
    },
    removeEmailCycleRecipient: flow(function* (
      scheduleId: string,
      scope: RemoveEmailCycleRecipientRPC.RequestEnums.Scope.Scope,
    ) {
      if (!store.recipientDetails) {
        throw Error(
          "recipientDetails cannot be null while calling removeEmailCycleRecipient action",
        );
      }

      store.loadingRemoveEmailCycleRecipient = true;
      store.removeEmailCycleRecipientRPCError = null;

      try {
        const apiClient = getAPIClient(store);
        const request = new RemoveEmailCycleRecipientRPC.Request(
          new LeoUUID(scheduleId),
          new LeoUUID(store.recipientDetails.emailId),
          scope,
        );

        const result: LeoRPCResult<
          RemoveEmailCycleRecipientRPC.Response,
          RemoveEmailCycleRecipientRPC.Errors.Errors
        > =
          yield useRemoveEmailCycleRecipientRPCClientImpl(apiClient).execute(
            request,
          );

        if ("response" in result) return;

        switch (result.error.code) {
          case RemoveEmailCycleRecipientRPC.RPCError.InvalidScheduleId:
          case RemoveEmailCycleRecipientRPC.RPCError.InvalidEmailId:
          case RemoveEmailCycleRecipientRPC.RPCError.ScheduleInProgress:
            store.removeEmailCycleRecipientRPCError = result.error.code;
            break;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingRemoveEmailCycleRecipient = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createRemoveEmailCycleRecipientStore = (): Instance<
  typeof RemoveEmailCycleRecipientStore
> => RemoveEmailCycleRecipientStore.create(getInitialState());
